import { gql as apolloGql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Box, Button, ButtonGroup, HStack, IconButton, Link, chakra, useMediaQuery } from '@chakra-ui/react';
import { displayNameForUser, isAiToolUser, UserAvatar } from '@src/components/sco/Account/UserAvatar';
import { useChatContext } from '@src/components/sco/Chat/ChatContext';
import { ChatMessageComposer } from '@src/components/sco/Chat/ChatMessageComposer';
import { stripMarkdownWhitespace } from '@src/components/sco/Chat/ChatMessageComposer';
import { ChatMessageList } from '@src/components/sco/Chat/ChatMessageList';
import { ChatThinkingPanel, COMMENT_THINKING_SELECTION, hasCommentThinking } from '@src/components/sco/Chat/ChatThinkingPanel';
import { AUTO_REPLY_LABEL, WITHDRAWN_COMMENT_PLACEHOLDER, commentShowsAutoReplyLabel, commentWithdrawnAt } from '@src/components/sco/Chat/commentPresentation';
import { shouldRevealAssistantText } from '@src/components/sco/Chat/revealText';
import { useRevealedText } from '@src/components/sco/Chat/useRevealedText';
import { CommentAttachmentChips, COMMENT_STORED_FILES_SELECTION } from '@src/components/sco/Comments/CommentAttachments';
import { CommentDeleter } from '@src/components/sco/Comments/CommentDeleter';
import { CommentEditor } from '@src/components/sco/Comments/CommentEditor';
import { CommentFlagger } from '@src/components/sco/Comments/CommentFlagger';
import { MarkdownRenderer } from '@src/components/sco/MarkdownRenderer';
import { ScoChatEmbedContext, type ScoQuestionAnswerPayload } from '@src/components/sco/MarkdownRenderer/ScoChatEmbedContext';
import { formatScoAnswerComment, isStructuredAnswerComment, shouldShowScoAnswerMachineTags, stripScoAnswerMachineTags } from '@src/components/sco/MarkdownRenderer/formatScoAnswerComment';
import { stripIncompleteScoEmbeds } from '@src/components/sco/MarkdownRenderer/stripIncompleteScoEmbeds';
import { ViewableTracker } from '@src/components/sco/ViewableTracker';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ScoComment } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import { useDebounce, useHover } from '@uidotdev/usehooks';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LuBrain, LuFlag, LuPencil, LuReply, LuThumbsUp, LuTrash } from 'react-icons/lu';

/** GraphQL selection for interactive embeds — keep query + subscription in sync. */
export const COMMENT_EMBEDS_SELECTION = `
  embedsAsJson
`;

// Apollo cache.writeFragment needs a real FragmentDefinition. The fraql `gql`
// from @src/lib/gql converts pure fragment documents to InlineFragment.
const SCO_COMMENT_EMBEDS_PATCH_FRAGMENT = apolloGql`
  fragment ScoCommentEmbedsPatch on ScoComment {
    embedsAsJson
  }
`;
type ChatMessageProps = {
  comment?: ScoComment;
  commentId?: string;
  inlineReplies?: boolean;
  nextComment?: ScoComment;
  priorComment?: ScoComment;
  showReplies?: boolean;
};
export const ChatMessage = (props: ChatMessageProps) => {
  const comment = props.comment;
  const chatContext = useChatContext();
  const {
    currentUser
  } = useCurrentUser();
  const isLoggedIn = !!currentUser?.isRegistered;
  const isAdmin = !!(currentUser as any)?.isAdmin || !!(currentUser as any)?.isScoAdmin;
  const [hoverRef, isHovering] = useHover();
  const [showReplies, setShowReplies] = useState(props.showReplies);
  const [showEditor, setShowEditor] = useState(false);
  const [showDeleter, setShowDeleter] = useState(false);
  const [showFlagger, setShowFlagger] = useState(false);
  /** User-revealed thinking for completed AI messages (brain hover action). */
  const [showThinking, setShowThinking] = useState(false);
  const isDeleted = useDebounce(!!comment?.deletedAt, 750);
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const isMobile = getBreakpointValue([true, true, false, false, false]);
  const isAiMessage = isAiToolUser(comment?.createdByUser);
  const unsent = Boolean(commentWithdrawnAt(comment));
  const autoReply = commentShowsAutoReplyLabel(comment);
  const thinking = comment?.thinking;
  const isThinkingStreaming = thinking?.status === 'streaming';
  const hasAnswerBody = !!stripMarkdownWhitespace(comment?.body || '');
  const [prefersReducedMotion] = useMediaQuery(['(prefers-reduced-motion: reduce)']);
  // First paint only. History and a refresh mid-reply already have text and must not replay.
  const [mountedEmpty] = useState(() => isAiMessage && !hasAnswerBody);

  // Escape hatch only: ?showScoAnswerTags=1 shows the human answer bubble + raw machine tags.
  // Do not use isAdmin alone — staff/admins still need the clean "answer on the AI card only" UX.
  const forceShowTags = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('showScoAnswerTags') === '1';
  const showMachineTags = shouldShowScoAnswerMachineTags({
    isAdmin,
    forceShow: forceShowTags
  });

  /**
   * Structured chip/field answers already lock on the AI message card.
   * Always hide the duplicate human bubble (body still stored for model history + embeds patch).
   * Only `?showScoAnswerTags=1` keeps the bubble visible for debugging.
   */
  const isHiddenStructuredAnswer = !isAiMessage && isStructuredAnswerComment(comment?.body) && !forceShowTags;
  const displayBody = useMemo(() => {
    const raw = comment?.body || '';
    if (isAiMessage) {
      // Belt-and-suspenders: never feed unclosed sco-* embeds to rehype while streaming.
      // Server partial sanitize should already hold these; this covers lag/regression.
      return stripIncompleteScoEmbeds(raw);
    }
    if (showMachineTags) {
      return raw;
    }
    return stripScoAnswerMachineTags(raw);
  }, [comment?.body, isAiMessage, showMachineTags]);
  const streamOpen = isThinkingStreaming;
  const revealedBody = useRevealedText(displayBody, shouldRevealAssistantText({
    isAiMessage,
    unsent,
    mountedEmpty,
    prefersReducedMotion,
    thinkingStatus: thinking?.status
  }), streamOpen);
  const visibleBody = isAiMessage ? revealedBody : displayBody;
  const visibleHasAnswer = !!stripMarkdownWhitespace(visibleBody);
  // Keep the live thinking row up until revealed answer text is actually on screen.
  const isLiveThinking = isThinkingStreaming && !(isAiMessage ? visibleHasAnswer : hasAnswerBody);
  const canRevealThinking = isAiMessage && !unsent && hasCommentThinking(thinking) && !isLiveThinking;
  const [upsertChatComment] = useMutation(ChatMessageComposer.mutations.upsertChatComment());
  const getQuestionState = useCallback((questionId: string) => {
    const embeds = comment?.embedsAsJson as {
      questions?: Array<{
        questionId: string;
        answer?: {
          optionIds?: string[];
          value?: string | number | null;
          valueJson?: unknown;
          freeText?: string;
          fieldType?: string;
        } | null;
        options?: Array<{
          id: string;
          label: string;
        }>;
      }>;
    } | null | undefined;
    const q = embeds?.questions?.find(x => (x.questionId || '').toLowerCase() === questionId.toLowerCase());
    if (!q) {
      return {
        answered: false,
        answer: null
      };
    }
    if (!q.answer) {
      return {
        answered: false,
        answer: null
      };
    }
    const optionLabels = q.answer.optionIds?.map(id => q.options?.find(o => o.id === id)?.label || id);
    return {
      answered: true,
      answer: {
        optionIds: q.answer.optionIds,
        value: q.answer.value,
        valueJson: q.answer.valueJson,
        freeText: q.answer.freeText,
        fieldType: q.answer.fieldType,
        optionLabels
      }
    };
  }, [comment?.embedsAsJson]);
  const postAnswer = useCallback(async (payload: ScoQuestionAnswerPayload) => {
    if (!comment?.threadId) {
      return;
    }
    const body = formatScoAnswerComment(payload);

    // Optimistically mark this AI comment's embeds answered so the soft-FA
    // banner and any other readers of embedsAsJson clear immediately
    // (subscription only fetchMores *new* comments, not AI embed patches).
    const priorEmbeds = comment.embedsAsJson as {
      version?: number;
      questions?: Array<Record<string, unknown> & {
        questionId?: string;
        answer?: unknown;
      }>;
    } | null | undefined;
    const optimisticEmbeds = priorEmbeds?.questions?.length ? {
      ...priorEmbeds,
      version: priorEmbeds.version ?? 1,
      questions: priorEmbeds.questions.map(q_0 => {
        if ((q_0.questionId || '').toLowerCase() !== payload.questionId.toLowerCase()) {
          return q_0;
        }
        if (q_0.answer != null) {
          return q_0;
        }
        return {
          ...q_0,
          answer: {
            fieldType: payload.fieldType,
            optionIds: payload.optionIds,
            value: payload.value ?? null,
            valueJson: payload.valueJson,
            freeText: payload.freeText,
            storedFileIds: payload.storedFileIds,
            currency: payload.currency,
            answeredAt: new Date().toISOString(),
            answeredByCommentId: 'optimistic',
            answeredByUserId: null
          }
        };
      })
    } : priorEmbeds;
    await upsertChatComment({
      variables: {
        input: {
          body,
          threadId: comment.threadId,
          parentCommentId: comment.parentCommentId || undefined,
          storedFileIds: payload.storedFileIds
        }
      },
      optimisticResponse: undefined,
      update(cache) {
        if (!comment.id || !optimisticEmbeds) {
          return;
        }
        try {
          cache.writeFragment({
            id: cache.identify({
              __typename: 'ScoComment',
              id: comment.id
            }),
            fragment: SCO_COMMENT_EMBEDS_PATCH_FRAGMENT,
            data: {
              __typename: 'ScoComment',
              embedsAsJson: optimisticEmbeds
            }
          });
        } catch (e) {
          console.warn('[ChatMessage] embedsAsJson cache patch failed', e);
        }
      }
    });
  }, [comment?.threadId, comment?.parentCommentId, comment?.id, comment?.embedsAsJson, upsertChatComment]);
  const getQuestionProgress = useCallback((questionId_0: string) => {
    const embeds_0 = comment?.embedsAsJson as {
      questions?: Array<{
        questionId: string;
      }>;
    } | null | undefined;
    const questions = embeds_0?.questions || [];
    if (questions.length <= 1) {
      return null;
    }
    const idx = questions.findIndex(q_1 => q_1.questionId === questionId_0);
    if (idx < 0) {
      return null;
    }
    return {
      index: idx + 1,
      total: questions.length
    };
  }, [comment?.embedsAsJson]);

  /**
   * Interactive only when the question is in embedsAsJson (mid-stream or finalize),
   * or the AI turn is no longer streaming (body-only / lag-safe fallback).
   */
  const isQuestionReady = useCallback((questionId_1: string) => {
    const id_0 = (questionId_1 || '').toLowerCase();
    if (!id_0) {
      return false;
    }
    const embeds_1 = comment?.embedsAsJson as {
      questions?: Array<{
        questionId: string;
      }>;
    } | null | undefined;
    if (embeds_1?.questions?.some(q_2 => (q_2.questionId || '').toLowerCase() === id_0)) {
      return true;
    }
    // Stream finished — allow body-rendered embeds even if JSON lagged one tick.
    return thinking?.status !== 'streaming';
  }, [comment?.embedsAsJson, thinking?.status]);
  const embedContextValue = useMemo(() => ({
    threadId: comment?.threadId,
    parentCommentId: comment?.parentCommentId,
    canSubmit: true,
    isQuestionReady,
    getQuestionState,
    getQuestionProgress,
    postAnswer
  }), [comment?.threadId, comment?.parentCommentId, isQuestionReady, getQuestionState, getQuestionProgress, postAnswer]);
  const [toggleLike, toggleLikeMutation] = useMutation(gql`
    mutation ($input: ToggleLikeInput!) {
      toggleLike(input: $input) {
        like {
          comment {
            id
            hasUserLiked
            likesCount
          }
        }
      }
    }
  `);
  const createdAtMoment = moment(comment.createdAt);
  const authorUserId = comment?.createdByUser?.userId;
  const hasFollowOnComments = props.nextComment && !props.nextComment.deletedAt && !!authorUserId && authorUserId === props.nextComment.createdByUser?.userId && createdAtMoment.isSame(moment(props.nextComment?.createdAt), 'day');
  const isFollowOnComment = props.priorComment && !props.priorComment.deletedAt && !!authorUserId && authorUserId === props.priorComment.createdByUser?.userId && createdAtMoment.isSame(moment(props.priorComment?.createdAt), 'day');
  useEffect(() => {
    if (!comment.parentCommentId && !props.inlineReplies && showReplies) {
      chatContext.setActiveParentCommentId(comment.commentId);
    }
  }, [showReplies]);

  // Auto-hide when answer body starts (or thinking finishes); brain button re-opens it.
  useEffect(() => {
    if (hasAnswerBody || !isThinkingStreaming) {
      setShowThinking(false);
    }
  }, [hasAnswerBody, isThinkingStreaming, comment?.commentId]);
  if (isDeleted) {
    return <></>;
  }

  // Structured answers lock on the AI card — don't also render a full user bubble
  // with machine tags / duplicate prose (still persisted server-side for the model).
  if (isHiddenStructuredAnswer) {
    return null;
  }

  // Extremely subtle, and the same for every viewer. Follow-on comments have no name row, so
  // the label sits above the body for them (same 14px base so the 0.75em size matches).
  const autoReplyLabel = autoReply ? <chakra.span color={'colorPalette.fg/50'} fontSize={isMobile ? '0.7em' : '0.75em'} fontWeight={'medium'}>
      {AUTO_REPLY_LABEL}
    </chakra.span> : null;
  return <Box ref={hoverRef} id={`chat-message-${comment.commentId}`} opacity={comment.deletedAt ? '0.25' : 1}
  // borderTop={'1px solid'}
  // borderColor={isFollowOnComment ? 'transparent' : 'purpleLightAlpha.100'}
  paddingBottom={hasFollowOnComments ? '0.25em' : '1em'} paddingLeft={isMobile ? '0' : '0.5em'} paddingRight={isMobile ? '1.5em' : '3em'} paddingTop={isFollowOnComment ? '0.25em' : '1em'} width={'full'} _hover={{
    bgColor: 'colorPalette.emphasized/10'
  }}>
      <ViewableTracker viewableId={comment.commentId} viewableType={'Comment'} />
      <Box float={'right'} height={'1em'} marginTop={isFollowOnComment ? '-1.5em' : '-2.25em'}>
        <ButtonGroup attached={true} borderRadius={'0'} opacity={isHovering ? 1 : 0} size={'sm'} transition={'all 100ms ease-out'}>
          {isLoggedIn && <IconButton aria-label={'Like'} disabled={toggleLikeMutation.loading} variant={comment.hasUserLiked ? 'cta' : 'solid'} onClick={() => {
          toggleLike({
            variables: {
              input: {
                likeableType: 'Comment',
                likeableId: comment.commentId
              }
            }
          });
        }}>
              <LuThumbsUp />
            </IconButton>}
          {canRevealThinking && <IconButton aria-label={showThinking ? 'Hide thinking' : 'Show thinking'} title={showThinking ? 'Hide thinking' : 'Show thinking'} variant={showThinking ? 'cta' : 'solid'} onClick={() => setShowThinking(v => !v)}>
              <LuBrain />
            </IconButton>}
          {/* Reply is for registered members; hide on anonymous/guest tool-chat hover chrome */}
          {isLoggedIn && !comment.parentCommentId && <IconButton aria-label={'Reply'} onClick={() => setShowReplies(!showReplies)}>
              <LuReply />
            </IconButton>}
          {comment.canUserEdit && <IconButton aria-label={'Edit'} onClick={() => setShowEditor(!showEditor)}>
              <LuPencil />
            </IconButton>}
          {comment.canUserFlag && <IconButton aria-label={'Flag/Report'} disabled={comment.hasUserFlagged} onClick={() => setShowFlagger(!showFlagger)}>
              <LuFlag />
            </IconButton>}
          {comment.canUserDelete && <IconButton aria-label={'Delete'} onClick={() => setShowDeleter(!showDeleter)}>
              <LuTrash />
            </IconButton>}
        </ButtonGroup>
      </Box>
      <HStack align={'flex-start'} gap={isMobile ? '0' : 2}>
        <Box minWidth={isMobile ? '2.5em' : '3em'} textAlign={'center'} width={isMobile ? '2.5em' : '3em'}>
          {isFollowOnComment ? <chakra.span color={'colorPalette.fg/60'} fontSize={'10px'} title={createdAtMoment.format('LLLL')}>
              {isHovering ? createdAtMoment.format('h:m A') : <br />}
            </chakra.span> : <UserAvatar avatarProps={{
          size: isMobile ? 'xs' : 'sm'
        }} isAI={isAiToolUser(comment.createdByUser)} user={comment.createdByUser} />}
        </Box>
        <Box flexGrow={1}>
          {!isFollowOnComment && <HStack fontSize={'14px'} paddingBottom={1}>
              <Link asChild color={'colorPalette.fg'} fontFamily={'Poppins'} fontWeight={'bold'} _hover={{
            textDecoration: 'underline'
          }}>
                <NextLink target={'_blank'} href={comment?.createdByUser?.userId && !comment.createdByUser?.isGuest ? `/members/${comment.createdByUser.userId}` : '#'}>
                  {displayNameForUser(comment.createdByUser, isAiToolUser(comment.createdByUser)) || comment.createdByUser?.fullName || comment.createdByUser?.displayName || (comment.createdByUser?.isGuest ? 'Guest' : 'User')}
                </NextLink>
              </Link>
              <chakra.span color={'colorPalette.fg/50'} fontFamily={'Poppins'} fontSize={isMobile ? '0.7em' : '0.85em'} fontWeight={'semibold'} letterSpacing={'-0.25px'} title={createdAtMoment.format('LLLL')}>
                {createdAtMoment.isSame(moment(), 'day') ? createdAtMoment.format('h:mm a') : createdAtMoment.format('MMM D • h:mm a')}
              </chakra.span>
              {autoReplyLabel}
            </HStack>}
          {isFollowOnComment && autoReplyLabel ? <Box fontSize={'14px'} paddingBottom={0.5}>
              {autoReplyLabel}
            </Box> : null}
          <Box width={'full'}>
            {showEditor ? <CommentEditor comment={props.comment} onCancel={() => setShowEditor(false)} onError={() => setShowEditor(false)} onSuccess={() => setShowEditor(false)} /> : <>
                {isAiMessage && !unsent ? <ChatThinkingPanel compact={isMobile} forceVisible={showThinking} hasAnswerBody={visibleHasAnswer} thinking={thinking} /> : null}
                {unsent ? <chakra.span color={'colorPalette.fg/60'} fontStyle={'italic'}>
                    {WITHDRAWN_COMMENT_PLACEHOLDER}
                  </chakra.span> : (isAiMessage ? visibleHasAnswer : hasAnswerBody) ? isAiMessage ? <ScoChatEmbedContext.Provider value={embedContextValue}>
                      <MarkdownRenderer baseFontSize={isMobile ? '0.95em' : '1em'} injectParagraphsIfBreaksNotPresent={true} preset={'chat-message'}>
                        {visibleBody}
                      </MarkdownRenderer>
                    </ScoChatEmbedContext.Provider> : <MarkdownRenderer baseFontSize={isMobile ? '0.95em' : '1em'} injectParagraphsIfBreaksNotPresent={true} preset={'chat-message'}>
                      {displayBody}
                    </MarkdownRenderer> : null}
                {unsent ? null : <CommentAttachmentChips files={comment.storedFiles?.items} />}
              </>}
          </Box>
          {(showReplies && props.inlineReplies || comment.childCommentsCount > 0 || comment.likesCount > 0) && <HStack paddingTop={'0.5em'}>
              {(showReplies && props.inlineReplies || comment.childCommentsCount > 0) && <Button borderRadius={'0.25em'} borderWidth={'1px'} size={'xs'} onClick={() => setShowReplies(!showReplies)} {...showReplies && props.inlineReplies ? {
            borderColor: 'transparent',
            variant: 'filled',
            bgColor: 'colorPalette.emphasized/50',
            marginBottom: '-2px'
          } : {
            variant: 'outline'
          }}>
                  <LuReply />
                  {comment.childCommentsCount || ''} Replies
                </Button>}
              {isLoggedIn && comment.likesCount > 0 && <Button borderRadius={'0.25em'} borderWidth={'1px'} disabled={toggleLikeMutation.loading} size={'xs'} variant={'outline'} onClick={() => {
            toggleLike({
              variables: {
                input: {
                  likeableType: 'Comment',
                  likeableId: comment.commentId
                }
              }
            });
          }}>
                  <LuThumbsUp />
                  {comment.likesCount}
                </Button>}
            </HStack>}

          {showReplies && props.inlineReplies && <Box bgColor={'backgroundSecondary'} borderRadius={'md'}>
              <ChatMessageList isInline={props.inlineReplies} parentCommentId={comment.commentId} threadId={comment.threadId} />

              <ChatMessageComposer boxProps={{
            padding: 4
          }} placeholder={'Type a reply...'} newCommentValues={{
            threadId: comment.threadId,
            parentCommentId: comment.commentId
          }} />
            </Box>}
        </Box>
      </HStack>
      <CommentDeleter commentId={comment.commentId} isOpen={showDeleter} onClose={() => setShowDeleter(false)} />
      <CommentFlagger commentId={comment.commentId} isOpen={showFlagger} onClose={() => setShowFlagger(false)} />
    </Box>;
};
ChatMessage.fragments = {
  ScoComment: () => gql`
    fragment _ on ScoComment {
      id
      commentId
      createdByUser {
        id
        userId
        firstName
        fullName
        displayName
        lastName
        pictureUrl
        ${UserAvatar.fragments.ScoUser()}
      }
      createdByUserId
      featuredAt
      createdAt
      deletedAt
      body
      withdrawnAt
      originKey
      ${COMMENT_THINKING_SELECTION}
      ${COMMENT_EMBEDS_SELECTION}
      canUserComment
      canUserDelete
      canUserEdit
      canUserFlag
      canUserLike
      hasUserFlagged
      hasUserLiked
      childCommentsCount
      parentCommentId
      likesCount
      threadId
      ${COMMENT_STORED_FILES_SELECTION}
    }
  `
};